import type { CSSProperties } from 'react';
import { PLATES, permitLine } from './lib.js';
import type { AdKind, AdPlate } from './types';

/**
 * The Home ad card as customers see it (docs/design-review/product-hero/preview.html `.ad3`):
 * 343 × 180, Ink edge and hard lift, the "Ad · advertiser" pill top-left, the headline on a solid
 * plate bottom-left with the permit line in mono under it, and the deal cap sticker overhanging the
 * top-right corner. Video plays muted (sound never).
 */
export function AdPreview({
  kind,
  advertiser,
  headline,
  plate,
  deal,
  permitText,
  clearanceCode,
  imageSrc,
  videoSrc,
}: {
  kind: AdKind;
  advertiser: string;
  headline: string;
  plate: AdPlate;
  deal: string | null;
  permitText: string | null;
  clearanceCode: string | null;
  imageSrc: string | null;
  videoSrc: string | null;
}) {
  const p = PLATES[plate] ?? PLATES.volt;
  const permit = permitLine({ permitText, clearanceCode });
  const name = advertiser.trim() || 'Advertiser';
  const head = headline.trim() || 'Your headline';
  return (
    <div className="ad-stage">
      <div
        className={`ad3 ${kind}`}
        role="img"
        aria-label={`Preview: Ad, ${name}, ${head}${kind === 'video' ? ', video' : ''}${deal ? `, ${deal}` : ''}`}
        style={{ '--ad-plate': `var(${p.token})` } as CSSProperties}
      >
        <div className="ad3-media">
          {kind === 'video' && videoSrc ? (
            <video src={videoSrc} poster={imageSrc ?? undefined} muted autoPlay loop playsInline aria-hidden="true" />
          ) : imageSrc ? (
            <img src={imageSrc} alt="" />
          ) : (
            <span className="ad3-empty t-monocap">{kind === 'video' ? 'Poster + video · 1.91:1' : 'Photo · 1200 × 628'}</span>
          )}
          {kind === 'video' ? <span className="ad3-bar" aria-hidden="true"><i /></span> : null}
        </div>
        <span className="ad3-pill">
          <b>Ad</b>
          <span>{name}</span>
        </span>
        <div className={p.dark ? 'ad3-plate dark' : 'ad3-plate'}>
          <b>{head}</b>
          {permit ? <small>{permit}</small> : null}
        </div>
        {deal ? <div className="ad3-deal">{deal}</div> : null}
        {kind === 'video' ? <span className="ad3-mute">NO SOUND</span> : null}
      </div>
    </div>
  );
}
